import { IContainerStrategy } from '../ContainerStrategy'
import {
    ContainerDataType,
    ContainerType,
    RoleType,
    SourceType,
    TaskMode,
    AttributesData,
    QCAttributesData,
    TaskConfigType,
    ValueDataType,
    AttributeDefinitionType,
    CustomInterfaceDataType,
} from 'Types/Tasks.types'
import { cloneDeep } from 'lodash'
import { isViewerMode } from 'Utils/task.utils'
import { CustomInterfaceTask } from 'Tasks/CustomInterfaceTask/CustomInterfaceTask'

export class InterfaceContainerStrategy
    implements IContainerStrategy<ContainerType.CustomInterface, CustomInterfaceDataType>
{
    containerContext?: {
        task: CustomInterfaceTask
    }

    setContext(containerContext: { task: CustomInterfaceTask }) {
        this.containerContext = containerContext
    }

    getEmptyDraftContainer(): ContainerDataType<
        ContainerType.CustomInterface,
        CustomInterfaceDataType
    > {
        return {
            data: {
                schema_version: 1,
                type: ContainerType.CustomInterface,
                data: {
                    response: {
                        id: crypto.randomUUID(),
                        role: RoleType.ASSISTANT,
                        source: SourceType.HUMAN,
                        state: {
                            value: {},
                            value_type: 'JSON',
                        },
                        extra_info: {},
                        version_id: null,
                    },
                    quality_check: {
                        evaluation_on_response_version_id: {},
                    },
                },
            },
        }
    }

    getContainerFromOutputStart(
        outputStartContainer: ContainerDataType<
            ContainerType.CustomInterface,
            CustomInterfaceDataType
        >,
        mode: TaskMode
    ): ContainerDataType<ContainerType.CustomInterface, CustomInterfaceDataType> {
        const draftContainer = cloneDeep(outputStartContainer)

        if (draftContainer && !isViewerMode(mode)) {
            draftContainer.data.data.quality_check.evaluation_on_response_version_id = {}
            draftContainer.decision = null
        }

        return draftContainer
    }

    getModifiedDraftOutputContainer = (
        draftOutputContainer: ContainerDataType<
            ContainerType.CustomInterface,
            CustomInterfaceDataType
        >,
        taskConfig: TaskConfigType<ContainerType.CustomInterface, CustomInterfaceDataType>
    ) => {
        const draftContainer = cloneDeep(draftOutputContainer)

        const extraInfo: AttributesData = {}
        const qcAttributes: { [versionId: string]: QCAttributesData } = {}

        if (!draftContainer) {
            return draftContainer
        }

        const versionIds = Object.keys(
            draftContainer.data.data.quality_check.evaluation_on_response_version_id ?? {}
        )

        taskConfig.assignment.task.attributes.forEach(attribute => {
            if (
                draftContainer.data.data.response.extra_info?.[attribute.name] &&
                attribute.type === AttributeDefinitionType.EXTRA_INFO
            ) {
                extraInfo[attribute.name] = draftContainer.data.data.response.extra_info?.[
                    attribute.name
                ] as ValueDataType<never>
            }

            versionIds.forEach(versionId => {
                if (
                    draftContainer.data.data.quality_check.evaluation_on_response_version_id?.[
                        versionId
                    ]?.[attribute.name] &&
                    attribute.type === AttributeDefinitionType.QUALITY_EVALUATION
                ) {
                    qcAttributes[versionId] = {
                        ...qcAttributes[versionId],
                        [attribute.name]:
                            draftContainer.data.data.quality_check
                                .evaluation_on_response_version_id[versionId][attribute.name],
                    } as QCAttributesData
                }
            })
        })
        draftContainer.data.data.response.extra_info = extraInfo
        draftContainer.data.data.quality_check.evaluation_on_response_version_id = qcAttributes

        draftContainer.data.data.response.state = {
            value: this.containerContext?.task.taskInterface.getStateStore().toJSON() ?? {},
            value_type: 'JSON',
        }

        return draftContainer
    }
}
